import React, { useEffect, useMemo, useRef, useState } from "react";

import SharedMediaViewer from "../../shared/mediaViewer/SharedMediaViewer";
import {
  CHAT_LIGHTBOX_SWIPE_THRESHOLD_PX,
  swipeDirection,
} from "./chatAttachmentQueue";

export { CHAT_LIGHTBOX_SWIPE_THRESHOLD_PX, swipeDirection };

const CHAT_VIEWER_TEST_IDS = {
  root: "chat-lightbox",
  backdrop: "chat-lightbox-backdrop",
  toolbar: "chat-lightbox-toolbar",
  canvas: "chat-lightbox-canvas",
  loading: "chat-lightbox-loading",
  error: "chat-lightbox-error",
  image: "chat-lightbox-image",
  navPrev: "chat-lightbox-nav-prev",
  navNext: "chat-lightbox-nav-next",
  preloadPrev: "chat-lightbox-preload-prev",
  preloadNext: "chat-lightbox-preload-next",
  counter: "chat-lightbox-counter",
};

export function collectLightboxImages(messages) {
  if (!Array.isArray(messages)) return [];
  return messages
    .filter((m) => m && m.image && !m.is_deleted)
    .map((m) => ({
      id: m.id,
      image: m.image,
      image_name: m.image_name || "",
    }));
}

export function indexOfLightboxImage(images, selectedImageId) {
  if (selectedImageId == null) return -1;
  return images.findIndex((img) => img.id === selectedImageId);
}

function toViewerItems(images) {
  return images.map((img) => ({
    id: img.id,
    src: img.image,
    alt: img.image_name || "",
    filename: img.image_name || "",
    downloadUrl: img.image,
  }));
}

export default function ChatCarouselModal({
  messages,
  selectedImageId,
  onClose,
  restoreFocusRef = null,
}) {
  const imageSignature = Array.isArray(messages)
    ? messages
      .map((m) => `${m?.id}:${m?.image || ""}:${m?.is_deleted ? 1 : 0}`)
      .join("|")
    : "";
  const images = useMemo(
    () => collectLightboxImages(messages),
    // Recollect only when identity/src/deleted flags change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imageSignature],
  );
  const items = useMemo(() => toViewerItems(images), [images]);
  const selectedIndex = indexOfLightboxImage(images, selectedImageId);
  const [navIndex, setNavIndex] = useState(null);
  const currentIdRef = useRef(null);
  const selectedIdRef = useRef(selectedImageId);
  const selectionChanged = selectedIdRef.current !== selectedImageId;
  if (selectionChanged) {
    selectedIdRef.current = selectedImageId;
  }

  useEffect(() => {
    setNavIndex(null);
  }, [selectedImageId]);

  const activeIndex = navIndex == null || selectionChanged
    ? (selectedIndex >= 0 ? selectedIndex : 0)
    : navIndex;
  const open = selectedIndex >= 0 && items.length > 0;
  const safeIndex = open
    ? Math.min(Math.max(activeIndex, 0), items.length - 1)
    : 0;

  useEffect(() => {
    if (!selectedImageId) {
      currentIdRef.current = null;
      return;
    }
    if (selectedIndex < 0) {
      onClose?.();
      currentIdRef.current = null;
      return;
    }
    const previousId = currentIdRef.current;
    if (previousId && !images.some((img) => img.id === previousId)) {
      onClose?.();
      currentIdRef.current = null;
      return;
    }
    currentIdRef.current = images[safeIndex]?.id ?? null;
  }, [images, selectedImageId, selectedIndex, onClose, safeIndex]);

  if (!open) {
    return null;
  }

  const current = items[safeIndex];

  return (
    <SharedMediaViewer
      items={items}
      activeIndex={safeIndex}
      onIndexChange={setNavIndex}
      onClose={onClose}
      allowDownload
      restoreFocusRef={restoreFocusRef}
      title={current?.filename || "Зображення"}
      testIds={CHAT_VIEWER_TEST_IDS}
    />
  );
}
